if (!customElements.get('milestone-timeline')) {
  customElements.define('milestone-timeline',
    class MilestoneTimeline extends HTMLElement {
      constructor() {
        super();
        this.slides = [];
        this.swiper = null;
        this.loadedSlides = new Set();
      }

      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.milestone-slide'));

        // Set up intersection observers
        this.setupLoadObserver();
        this.setupPlayObserver();

        // Wait for Swiper to initialize
        this.initWhenReady();
      }

      disconnectedCallback() {
        if (this.loadObserver) this.loadObserver.disconnect();
        if (this.playObserver) this.playObserver.disconnect();
      }

      initWhenReady() {
        const swiperEl = this.querySelector('.swiper');
        if (!swiperEl) return;

        // Check if swiper is already initialized
        if (swiperEl.swiper) {
          this.swiper = swiperEl.swiper;
          this.onSwiperReady();
        } else {
          // Wait for swiper to initialize
          const observer = new MutationObserver(() => {
            if (swiperEl.swiper) {
              this.swiper = swiperEl.swiper;
              this.onSwiperReady();
              observer.disconnect();
            }
          });
          observer.observe(swiperEl, { attributes: true, childList: true, subtree: true });
        }
      }

      onSwiperReady() {
        // Load videos for initially visible slides and next slide
        this.preloadNextSlideVideo();

        // Listen for slide changes to preload next video
        this.swiper.on('slideChange', () => {
          this.preloadNextSlideVideo();
        });
      }

      /**
       * Observer to load videos when slides are about to become visible
       * Uses a larger rootMargin to preload before the slide is fully in view
       */
      setupLoadObserver() {
        this.loadObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.loadSlideMedia(entry.target);
              }
            });
          },
          {
            root: null,
            rootMargin: '200px 100% 200px 100%',
            threshold: 0
          }
        );

        this.slides.forEach((slide) => {
          this.loadObserver.observe(slide);
        });
      }

      /**
       * Observer to play/pause videos based on visibility
       * Play 100px before visible on Y axis, only when visible on X axis
       * Pause when completely out of view
       */
      setupPlayObserver() {
        this.playObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target.querySelector('video');
              if (!video) return;

              if (entry.isIntersecting) {
                this.playVideo(video);
              } else {
                this.pauseVideo(video);
              }
            });
          },
          {
            root: null,
            rootMargin: '100px 0px 100px 0px',
            threshold: 0
          }
        );

        this.slides.forEach((slide) => {
          this.playObserver.observe(slide);
        });
      }

      /**
       * Preload video for the next slide in the carousel
       */
      preloadNextSlideVideo() {
        if (!this.swiper) return;

        const activeIndex = this.swiper.activeIndex;
        const slidesPerView = this.swiper.params.slidesPerView;
        const endIndex = activeIndex + Math.ceil(slidesPerView) + 1;

        for (let i = activeIndex; i <= endIndex && i < this.slides.length; i++) {
          this.loadSlideMedia(this.slides[i]);
        }
      }

      /**
       * Load video and image for a slide
       */
      loadSlideMedia(slide) {
        if (!slide || this.loadedSlides.has(slide)) return;

        // Load video
        const video = slide.querySelector('video');
        if (video) {
          const sources = video.querySelectorAll('source[data-src]');
          sources.forEach((source) => {
            source.src = source.dataset.src;
          });
          if (sources.length > 0) video.load();
        }

        // Load image by changing lazy to eager
        const img = slide.querySelector('img[loading="lazy"]');
        if (img) {
          img.loading = 'eager';
        }

        this.loadedSlides.add(slide);
      }

      /**
       * Play video if it's loaded and ready
       */
      playVideo(video) {
        if (!video) return;

        const sources = video.querySelectorAll('source');
        const hasLoadedSource = Array.from(sources).some(s => s.src && s.src !== '');
        if (!hasLoadedSource) return;

        const checkPlaying = () => {
          if (video.currentTime > 0.1) {
            video.classList.add('video--is-playing');
            video.removeEventListener('timeupdate', checkPlaying);
          }
        };
        video.addEventListener('timeupdate', checkPlaying);

        if (video.readyState >= 2) {
          video.play().catch(() => {});
        } else {
          video.addEventListener('loadeddata', () => {
            video.play().catch(() => {});
          }, { once: true });
        }
      }

      /**
       * Pause video
       */
      pauseVideo(video) {
        if (!video) return;
        video.pause();
      }
    }
  );
}
